/**
 * The join symbol, drawn module by module into a canvas.
 *
 * Deliberately black on white in both themes. A QR symbol is not UI: decoders
 * expect dark modules on a light ground, and while many will cope with an
 * inverted symbol, "many" is the wrong reliability for a phone camera pointed
 * across a dim table at a screen. The frame around it is themed; the symbol
 * itself is a machine-readable object and stays the colour machines expect.
 *
 * If the URL does not fit the encoder's supported versions the canvas renders
 * nothing and reports it, so the caller can fall back to the code in large type
 * — which is what most people at a table end up using anyway.
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { encodeQr } from "./qr.ts";

export interface QrCanvasProps {
  /** The URL a guest device should open. */
  text: string;
  /** Target pixel size of the drawn symbol, quiet zone included. */
  size?: number;
  /** Called with false when the text could not be encoded. */
  onEncoded?: (ok: boolean) => void;
}

/** Four modules of light margin; below that, scanners start missing symbols. */
const QUIET = 4;

export function QrCanvas({ text, size = 240, onEncoded }: QrCanvasProps): ReactNode {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const symbol = encodeQr(text);
    if (!symbol) {
      onEncoded?.(false);
      const blank = canvas.getContext("2d");
      blank?.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const total = symbol.size + QUIET * 2;
    // Round the module size down to whole device pixels: a fractional module
    // lands on half a pixel and the anti-aliasing is what makes a symbol fail
    // to scan far more often than contrast does.
    const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    const scale = Math.max(1, Math.floor((size * dpr) / total));
    const px = total * scale;

    canvas.width = px;
    canvas.height = px;
    canvas.style.width = `${px / dpr}px`;
    canvas.style.height = `${px / dpr}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      onEncoded?.(false);
      return;
    }

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, px, px);
    ctx.fillStyle = "#000000";
    for (let row = 0; row < symbol.size; row++) {
      for (let col = 0; col < symbol.size; col++) {
        if (!symbol.modules[row * symbol.size + col]) continue;
        ctx.fillRect((col + QUIET) * scale, (row + QUIET) * scale, scale, scale);
      }
    }
    onEncoded?.(true);
  }, [text, size, onEncoded]);

  return <canvas ref={ref} className="pb-qr" role="img" aria-label={`Join code link: ${text}`} />;
}
